import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

export type EmbedTheme = 'dark' | 'light'

function detectEmbed(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('embed') === '1') return true
    return window.self !== window.top
  } catch {
    return false
  }
}

function initialTheme(searchParams: URLSearchParams): EmbedTheme {
  const t = searchParams.get('theme')
  if (t === 'dark' || t === 'light') return t
  // Embeds sit inside Filament (usually dark). Prefer dark when theme not set
  // so we never flash white islands while waiting for postMessage.
  if (detectEmbed()) {
    try {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    } catch { /* ignore */ }
    return 'dark'
  }
  return 'light'
}

/**
 * Filament embed theme: ?theme= from iframe URL, then live updates via
 * postMessage { type: 'beacon-theme', theme: 'dark'|'light' }.
 */
export function useEmbedTheme(): EmbedTheme {
  const [searchParams] = useSearchParams()
  const [theme, setTheme] = useState<EmbedTheme>(() => initialTheme(searchParams))

  useEffect(() => {
    setTheme(initialTheme(searchParams))
  }, [searchParams])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const data = event.data
      if (!data || typeof data !== 'object') return
      if (data.type !== 'beacon-theme') return
      if (data.theme === 'dark' || data.theme === 'light') {
        setTheme(data.theme)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return theme
}
